import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import PickerField from './PickerField';
import Popover from './Popover';
import { withMinutes } from './calendarUtils';
import { formatTime, parseTimeInput, toDate } from '../../utils/dates';

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const pad = (value) => String(value).padStart(2, '0');

const TimePicker = ({
    value,
    onChange,
    step = 15,
    minTime,
    maxTime,
    placeholder = '--:--',
    disabled = false,
    invalid = false,
    clearable = false,
    size = 'md',
    className,
    ariaLabel = 'Time',
}) => {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState(null);
    const anchorRef = useRef(null);
    const baseId = useId();
    const popupId = `${baseId}-popup`;

    const date = toDate(value);
    const selectedMinutes = date ? date.getHours() * 60 + date.getMinutes() : null;

    const minMinutes = useMemo(() => {
        const bound = toDate(minTime);
        return bound ? bound.getHours() * 60 + bound.getMinutes() : null;
    }, [minTime]);

    const maxMinutes = useMemo(() => {
        const bound = toDate(maxTime);
        return bound ? bound.getHours() * 60 + bound.getMinutes() : null;
    }, [maxTime]);

    const minuteOptions = useMemo(
        () => Array.from({ length: Math.ceil(60 / step) }, (_, index) => index * step),
        [step]
    );

    const isOutOfBounds = useCallback((minutes) => {
        if (minMinutes !== null && minutes < minMinutes) return true;
        if (maxMinutes !== null && minutes > maxMinutes) return true;
        return false;
    }, [minMinutes, maxMinutes]);

    // An hour is only unreachable when every minute option inside it is.
    const isHourDisabled = useCallback(
        (hour) => minuteOptions.every(minute => isOutOfBounds(hour * 60 + minute)),
        [minuteOptions, isOutOfBounds]
    );

    const close = useCallback((reason) => {
        setOpen(false);
        setDraft(null);
        if (reason === 'escape' || reason === 'select') {
            anchorRef.current?.querySelector('.dt-field__input')?.focus();
        }
    }, []);

    // Drop a stale draft if the value changes underneath us (e.g. the paired
    // date picker rebuilt the Date).
    useEffect(() => setDraft(null), [value]);

    const commit = useCallback((minutes, shouldClose) => {
        onChange(withMinutes(date, minutes));
        if (shouldClose) close('select');
    }, [onChange, date, close]);

    // Changing the hour keeps the current minute where it can, and otherwise
    // lands on the first reachable minute inside that hour.
    const handleHour = (hour) => {
        if (selectedMinutes !== null) {
            const candidate = hour * 60 + (selectedMinutes % 60);
            if (!isOutOfBounds(candidate)) {
                commit(candidate, false);
                return;
            }
        }
        const fallback = minuteOptions.find(option => !isOutOfBounds(hour * 60 + option)) ?? 0;
        commit(hour * 60 + fallback, false);
    };

    const handleMinute = (minute) => {
        const hour = selectedMinutes === null ? new Date().getHours() : Math.floor(selectedMinutes / 60);
        commit(hour * 60 + minute, true);
    };

    // Also runs on blur, including when focus moves into the panel — so it must
    // stay silent unless the typed value actually differs, or every interaction
    // would emit a redundant onChange and mark the surrounding form dirty.
    const handleInputCommit = (raw) => {
        setDraft(null);
        if (!raw.trim()) {
            if (clearable && date) onChange(null);
            return;
        }
        const minutes = parseTimeInput(raw);
        if (minutes === null || isOutOfBounds(minutes)) return; // revert to the current value
        if (minutes === selectedMinutes) return;
        commit(minutes, false);
    };

    const selectedHour = selectedMinutes === null ? null : Math.floor(selectedMinutes / 60);
    const selectedMinute = selectedMinutes === null ? null : selectedMinutes % 60;

    return (
        <>
            <PickerField
                ref={anchorRef}
                icon="fa-clock"
                value={draft ?? formatTime(date)}
                placeholder={placeholder}
                open={open}
                popupId={popupId}
                disabled={disabled}
                invalid={invalid}
                clearable={clearable}
                editable
                size={size}
                className={className}
                ariaLabel={ariaLabel}
                onOpen={() => setOpen(true)}
                onClear={() => onChange(null)}
                onInputChange={setDraft}
                onInputCommit={handleInputCommit}
            />

            <Popover
                anchorRef={anchorRef}
                id={popupId}
                open={open}
                onClose={close}
                className="dt-popover--time"
            >
                <div className="dt-time">
                    <div className="dt-time__readout" aria-live="polite">
                        {selectedMinutes === null ? '--:--' : `${pad(selectedHour)}:${pad(selectedMinute)}`}
                    </div>

                    <div className="dt-time__section">
                        <span className="dt-time__label">Hour</span>
                        <div className="dt-time__grid dt-time__grid--hours">
                            {HOURS.map(hour => (
                                <button
                                    key={hour}
                                    type="button"
                                    disabled={isHourDisabled(hour)}
                                    aria-pressed={hour === selectedHour}
                                    className={`dt-time__cell ${hour === selectedHour ? 'is-selected' : ''}`.trim()}
                                    onClick={() => handleHour(hour)}
                                >
                                    {pad(hour)}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="dt-time__section">
                        <span className="dt-time__label">Minute</span>
                        <div className="dt-time__grid dt-time__grid--minutes">
                            {minuteOptions.map(minute => (
                                <button
                                    key={minute}
                                    type="button"
                                    disabled={selectedHour !== null && isOutOfBounds(selectedHour * 60 + minute)}
                                    aria-pressed={minute === selectedMinute}
                                    className={`dt-time__cell ${minute === selectedMinute ? 'is-selected' : ''}`.trim()}
                                    onClick={() => handleMinute(minute)}
                                >
                                    {pad(minute)}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </Popover>
        </>
    );
};

export default TimePicker;
